﻿import { useCallback, useEffect, useMemo, useState } from "react"
import {
  buildContactListDraft,
  parseContactList,
  parseMuteListEvent,
  parseRelayListEvent,
  type RelayListEntry,
} from "@/nostr/api/social"
import { KINDS, type NostrEvent } from "@/nostr/types"
import { useAuth } from "@/state/AuthContext"
import { useEventVersion, useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { preferredLookupRelays } from "@/utils/url"
import { usePublish } from "./usePublish"

export { useBookmarks } from "@/state/BookmarkContext"
export type { BookmarksResult } from "@/state/BookmarkContext"

export interface FollowListResult {
  following: string[]
  followingSet: Set<string>
  event?: NostrEvent
  loading: boolean
}

/** Reads the kind 3 contact list of any pubkey. */
export function useFollowList(target?: string): FollowListResult {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const [loading, setLoading] = useState(Boolean(target))

  useEffect(() => {
    if (!target) {
      setLoading(false)
      return
    }
    setLoading(true)
    const subscription = pool.subscribe(
      { kinds: [KINDS.CONTACT_LIST], authors: [target], limit: 1 },
      {
        relays: preferredLookupRelays(appRelays),
        onEvent: (event) => store.add(event),
        onEose: () => setLoading(false),
      },
    )
    return () => subscription.close()
  }, [target, pool, store, appRelays])

  return useMemo(() => {
    const event = target ? store.getReplaceable(KINDS.CONTACT_LIST, target) : undefined
    const following = parseContactList(event)
    return { following, followingSet: new Set(following), event, loading }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, version, loading, store])
}

/** The signed-in user's follow list plus a follow / unfollow action. */
export function useFollowing() {
  const { pubkey, canSign } = useAuth()
  const { publishDraft } = usePublish()
  const { following, followingSet, event, loading } = useFollowList(pubkey)
  const [pending, setPending] = useState<string>()

  const toggleFollow = useCallback(
    async (targetPubkey: string): Promise<boolean> => {
      if (!canSign) return false
      const wasFollowing = followingSet.has(targetPubkey)
      const next = wasFollowing
        ? following.filter((item) => item !== targetPubkey)
        : [...following, targetPubkey]
      setPending(targetPubkey)
      try {
        const outcome = await publishDraft(buildContactListDraft(next, event?.tags ?? []), {
          successMessage: wasFollowing ? "Unfollowed" : "Following",
        })
        return outcome.ok
      } finally {
        setPending(undefined)
      }
    },
    [canSign, following, followingSet, event, publishDraft],
  )

  return { following, followingSet, loading, toggleFollow, pending }
}

/**
 * Mute list (kind 10000) and NIP-65 relay list (kind 10002) of a user.
 *
 * Both lists are requested in **one** REQ (two filters) on the lookup relays and
 * the subscription closes after EOSE, so opening a profile costs a single extra
 * round trip instead of two. The mute list is read-only: it is counted and
 * displayed, never published (there is no mute editor yet).
 */
export function useProfileLists(pubkey?: string): {
  mute: { count: number; pubkeys: string[]; hashtags: string[]; words: string[] }
  relayEntries: RelayListEntry[]
  loading: boolean
} {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const [loading, setLoading] = useState(Boolean(pubkey))

  useEffect(() => {
    if (!pubkey) {
      setLoading(false)
      return
    }
    let subscription: { close: () => void } | undefined
    setLoading(true)
    subscription = pool.subscribe(
      [
        { kinds: [KINDS.MUTE_LIST], authors: [pubkey], limit: 1 },
        { kinds: [KINDS.RELAY_LIST], authors: [pubkey], limit: 1 },
      ],
      {
        relays: preferredLookupRelays(appRelays),
        onEvent: (event) => store.add(event),
        onEose: () => {
          subscription?.close()
          setLoading(false)
        },
      },
    )
    return () => subscription?.close()
  }, [pubkey, pool, store, appRelays])

  return useMemo(() => {
    const muteEvent = pubkey ? store.getReplaceable(KINDS.MUTE_LIST, pubkey) : undefined
    const relayEvent = pubkey ? store.getReplaceable(KINDS.RELAY_LIST, pubkey) : undefined
    const parsed = parseMuteListEvent(muteEvent)
    return {
      mute: {
        count: parsed.pubkeys.length + parsed.hashtags.length + parsed.words.length,
        pubkeys: parsed.pubkeys,
        hashtags: parsed.hashtags,
        words: parsed.words,
      },
      relayEntries: parseRelayListEvent(relayEvent),
      loading,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, version, loading, store])
}

/** Mute list only — prefer `useProfileLists` when both lists are needed. */
export function useMuteList(pubkey?: string) {
  return useProfileLists(pubkey).mute
}

/** NIP-65 relay list only — prefer `useProfileLists` when both lists are needed. */
export function useRelayList(pubkey?: string): { entries: RelayListEntry[]; loading: boolean } {
  const lists = useProfileLists(pubkey)
  return { entries: lists.relayEntries, loading: lists.loading }
}

